// Extracted from pages/Articles/CreateArticle.js (behavior-preserving move).
// Functions are recreated on every render with the latest wizard state, exactly as
// they were when they lived inside the page component. `log` is the original no-op.
import { useCallback } from 'react';
import { directusAPI } from '../../../api';
import { isLocalhost } from '../lib/workerEndpoints';
import {
  getStoredToken,
  isApiServerAvailable,
  log,
  markArticleFailed,
} from '../services/articleGenerationApi';

/**
 * Summary content type: links the selected (or automatically searched) scientific
 * article and lets the queue worker (function calling) produce the summary.
 */
export function useSummaryGeneration(ctx) {
  const {
    navigate,
    queueAPI,
    createArticleRecord,
    articleContext,
    keywords,
    sources,
    contentType,
    toneOfVoice,
    complexity,
    language,
    selectedProject,
    includeImages,
    generatedArticleId,
    selectedArticleForSummary,
    knowledgeMode,
    setGeneratedArticleId,
    setIsGenerationCompleted,
    setIsGenerating,
    setSaveError,
  } = ctx;

  // Function to generate summary using workers system
  const handleSummaryGeneration = useCallback(async () => {
    let articleId = generatedArticleId;

    try {
      log('\n🚀 ============ WORKERS SUMMARY GENERATION START ============');
      log('📋 SUMMARY: Starting summary generation process');
      log('🔍 SUMMARY: Selected article at start:', {
        hasSelectedArticle: !!selectedArticleForSummary,
        selectedArticleTitle: selectedArticleForSummary?.title?.substring(0, 50),
        selectedArticleId: selectedArticleForSummary?.id
      });

      // Skip generating modal for summary - everything happens in background
      log('🎯 SUMMARY: Starting background summary generation (no loading modal)');
            // Check if API server is available
            log('🔍 SUMMARY: Checking API server availability...');
          const useApi = await isApiServerAvailable();

          log('📊 SUMMARY: Detection results:', {
            apiAvailable: useApi,
            queueAvailable: queueAPI.isQueueAvailable(),
            isLocalhost: isLocalhost()
          });

          // For summary, ALWAYS use queue/worker system (has function calling)
          log('🎯 SUMMARY: ✅ Using Worker System for summary generation (function calling enabled)');
          log('🔍 SUMMARY: Knowledge mode:', knowledgeMode);

          if (knowledgeMode === 'manual') {
            if (selectedArticleForSummary) {
              log('📄 SUMMARY: Manual mode - Selected article:', {
                title: selectedArticleForSummary.title,
                pmcid: selectedArticleForSummary.id,
                authors: selectedArticleForSummary.authors,
                journal: selectedArticleForSummary.journal
              });
            } else {
              log('❌ SUMMARY: Manual mode but no article selected!');
              throw new Error('Manual mode requires selecting a specific article. Please search and select an article first.');
            }
          } else {
            log('🤖 SUMMARY: Automatic mode - AI will find best article based on topic:', articleContext);
          }

          // Create article record with selected article info
          log('📋 SUMMARY: Creating article record in Directus');
          log('📋 SUMMARY: Current state before creation:', {
            contentType,
            articleContext,
            keywords,
            sources,
            toneOfVoice,
            complexity,
            language,
            selectedProject,
            includeImages
          });

          const newArticle = await createArticleRecord();
          articleId = newArticle.id;
          log('✅ SUMMARY: Article created with ID:', articleId);
          log('✅ SUMMARY: Full article object:', newArticle);

          // Set article status to "ongoing" IMMEDIATELY after creation
          try {
            log('🔒 SUMMARY: Setting article status to ongoing (IMMEDIATE)');
            await directusAPI.patch(`/items/articles/${articleId}`, {
              status: 'ongoing'
            }, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getStoredToken()}`
              }
            });
            log('✅ SUMMARY: Article status set to ongoing (IMMEDIATE)');
          } catch (error) {
            log('❌ SUMMARY: Error setting immediate ongoing status:', error);
          }

          // Save knowledge mode info to Directus via sources field
          try {
            log('🚨🚨🚨 ABSOLUTE FRONTEND DEBUG - THIS SHOULD ALWAYS APPEAR! 🚨🚨🚨');
            log('💾 SUMMARY: Saving knowledge mode info to Directus via sources field');
            log('🔍 SUMMARY: Knowledge mode check:', {
              knowledgeMode,
              isManual: knowledgeMode === 'manual',
              selectedArticleExists: !!selectedArticleForSummary,
              selectedArticleId: selectedArticleForSummary?.id
            });
            log('🚨🚨🚨 ABOUT TO CHECK IF MANUAL MODE 🚨🚨🚨');

            if (knowledgeMode === 'manual') {
              log('🚀 SUMMARY: Starting MANUAL mode - saving to articles_sources collection');

                              // 1. First, save the selected article to articles_sources collection
                const articleSourceData = {
                  article_title: selectedArticleForSummary.title,
                  article_body: {
                    body: selectedArticleForSummary.fullText || selectedArticleForSummary.abstract || ''
                  },
                  article_abstract: {
                    abstract: selectedArticleForSummary.abstract || ''
                  },
                  article_author: selectedArticleForSummary.authors || '',
                  article_link: selectedArticleForSummary.url || selectedArticleForSummary.abstract_url || '',
                  article_date: selectedArticleForSummary.pubDate ?
                    new Date(selectedArticleForSummary.pubDate).toISOString() :
                    new Date().toISOString()
                };

              log('💾 SUMMARY: Saving article to articles_sources collection:', {
                title: articleSourceData.article_title?.substring(0, 50),
                hasBody: !!articleSourceData.article_body.body,
                hasAbstract: !!articleSourceData.article_abstract.abstract,
                bodyLength: articleSourceData.article_body.body.length,
                abstractLength: articleSourceData.article_abstract.abstract.length
              });

              log('🔍 SUMMARY: Full articleSourceData being sent:', JSON.stringify(articleSourceData, null, 2));

              const sourceResponse = await directusAPI.post('/items/articles_sources', articleSourceData, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getStoredToken()}`
                }
              });

              const sourceId = sourceResponse.data.data.id;
                              log('✅ SUMMARY: Article saved to articles_sources with ID:', sourceId);
                log('🔍 SUMMARY: Full sourceResponse:', JSON.stringify(sourceResponse.data, null, 2));

                // 2. Now update the main article with the linked source ID
                log('🔗 SUMMARY: Linking source to article via articles_linked_source...');
                log('🔗 SUMMARY: About to PATCH article', articleId, 'with sourceId:', sourceId);

              // Create junction table entry - try articles_articles_sources first (working table)
              let junctionResponse;
              try {
                log('🔗 SUMMARY: Trying articles_articles_sources table...');
                junctionResponse = await directusAPI.post('/items/articles_articles_sources', {
                  articles_id: articleId,
                  articles_sources_id: sourceId
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getStoredToken()}`
                  }
                });
                log('✅ SUMMARY: articles_articles_sources worked!');
              } catch (firstError) {
                log('❌ SUMMARY: articles_articles_sources failed, trying articles_linked_source...');
                junctionResponse = await directusAPI.post('/items/articles_linked_source', {
                  articles_id: articleId,
                  articles_sources_id: sourceId
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getStoredToken()}`
                  }
                });
              }

              // Update article_context separately
              const patchResponse = await directusAPI.patch(`/items/articles/${articleId}`, {
                article_context: `SELECTED_ARTICLE_MODE_WITH_FULL_DATA:${selectedArticleForSummary.id}:${articleContext}`
              }, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getStoredToken()}`
                }
              });

              log('✅ SUMMARY: Junction table created:', {
                junctionId: junctionResponse.data.data.id,
                articleId,
                sourceId
              });

              log('✅ SUMMARY: Article context updated:', {
                articleId,
                sourceId,
                status: patchResponse.status,
                responseData: patchResponse.data
              });

              log('🔍 SUMMARY: Verifying link was saved...');
              const verifyResponse = await directusAPI.get(`/items/articles/${articleId}?fields=articles_linked_source`);
              log('✅ SUMMARY: Link verification:', verifyResponse.data.data.articles_linked_source);
              log('✅ SUMMARY: Manual mode - Article linked to source successfully');
            } else {
              await directusAPI.patch(`/items/articles/${articleId}`, {
                sources: [{
                  summary_mode: 'automatic_search', // Flag to indicate automatic mode
                  search_query: articleContext, // What to search for
                  isAutomaticSummary: true // Additional flag
                }],
                article_context: `AUTOMATIC_ARTICLE_MODE:${articleContext}` // Encode mode in context
              }, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getStoredToken()}`
                }
              });
              log('✅ SUMMARY: Automatic mode - Search info saved');
            }
                      } catch (error) {
              log('❌ SUMMARY: Error saving knowledge mode info:', error);
              log('❌ SUMMARY: Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                config: {
                  url: error.config?.url,
                  method: error.config?.method,
                  data: error.config?.data
                }
              });

              // CRITICAL: Show exact Directus error message
              log('🚨 DIRECTUS ERROR RESPONSE DATA:', JSON.stringify(error.response?.data, null, 2));
              log('🚨 REQUEST PAYLOAD THAT FAILED:', JSON.stringify(error.config?.data, null, 2));
          }

          // Status already set to "ongoing" immediately after creation above

                  // Add to queue for worker processing (uses function calling)
        log('📤 SUMMARY: Adding summary to queue for worker processing');
        log('📤 SUMMARY: newArticle object:', newArticle);

        try {
          await queueAPI.addToQueue(newArticle);
          log('✅ SUMMARY: Successfully added to queue');
          setGeneratedArticleId(articleId);
          setIsGenerationCompleted(false);
          setIsGenerating(true);
        } catch (queueError) {
          log('❌ SUMMARY: Error adding to queue:', queueError);
          log('❌ SUMMARY: Queue error details:', queueError.message);
          throw queueError;
        }

        log('⏱️ SUMMARY: Starting status polling');
          const completedArticle = await queueAPI.pollStatus(articleId, (article) => {
            log('📊 SUMMARY: Status update:', article.queue_status);
          });

          log('✅ SUMMARY: Summary generation completed via worker:', completedArticle.id);

          // Set article status back to "completed"
          try {
            log('🔒 SUMMARY: Setting article status to completed');
            await directusAPI.patch(`/items/articles/${articleId}`, {
              status: 'completed'
            }, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getStoredToken()}`
              }
            });
            log('✅ SUMMARY: Article status set to completed');
          } catch (error) {
            log('❌ SUMMARY: Error setting completed status:', error);
          }

          setIsGenerating(false);
          setIsGenerationCompleted(true);
          log('✅ ============ WORKERS SUMMARY GENERATION END ============\n');

        } catch (error) {
          log('❌ SUMMARY ERROR:', error.message);
          log('❌ SUMMARY ERROR STACK:', error.stack);
          setSaveError('Summary generation failed: ' + error.message);
          setIsGenerating(false);
          setIsGenerationCompleted(false);
          await markArticleFailed(articleId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createArticleRecord/isApiServerAvailable/queueAPI are module-stable helpers
  }, [articleContext, keywords, sources, contentType, toneOfVoice, complexity, language, selectedProject, includeImages, navigate, generatedArticleId, selectedArticleForSummary, knowledgeMode]);

  return handleSummaryGeneration;
}
